import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cx, focusRingClass, interactiveClass } from './shared';
import { buildTabId, buildTabPanelId } from './tabIds';

export interface TabItem {
  id: string;
  label: string;
  description?: string;
  panel?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: readonly TabItem[];
  value: string;
  onValueChange: (id: string) => void;
  ariaLabel: string;
  idPrefix?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export function Tabs({
  tabs,
  value,
  onValueChange,
  ariaLabel,
  idPrefix,
  orientation = 'horizontal',
  className,
}: TabsProps) {
  const generatedId = useId();
  const prefix = idPrefix ?? `tabs-${generatedId}`;
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const enabledTabs = tabs.filter((tab) => !tab.disabled);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: string) => {
    const previousKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (![previousKey, nextKey, 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = enabledTabs.findIndex((tab) => tab.id === id);
    let nextIndex = currentIndex;
    if (event.key === nextKey) nextIndex = (currentIndex + 1) % enabledTabs.length;
    if (event.key === previousKey) {
      nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    }
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = enabledTabs.length - 1;

    const nextTab = enabledTabs[nextIndex];
    if (!nextTab) return;
    onValueChange(nextTab.id);
    tabRefs.current[nextTab.id]?.focus();
  };

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        className={cx(
          'flex gap-inline-control-gap rounded-surface border border-border-subtle bg-surface-recessed p-2',
          orientation === 'horizontal' ? 'flex-wrap' : 'flex-col',
        )}
      >
        {tabs.map((tab) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                tabRefs.current[tab.id] = node;
              }}
              id={buildTabId(prefix, tab.id)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={buildTabPanelId(prefix, tab.id)}
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              data-so-selected={selected}
              className={cx(
                focusRingClass,
                interactiveClass,
                'flex min-h-control-comfortable min-w-40 flex-col justify-center rounded-control border px-3 py-2 text-left disabled:cursor-not-allowed disabled:text-ink-muted',
                selected
                  ? 'border-accent bg-surface text-accent-ink shadow-raised'
                  : 'border-transparent bg-transparent text-ink hover:border-border-strong hover:bg-surface',
              )}
              onClick={() => onValueChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, tab.id)}
            >
              <span className="text-control font-semibold">{tab.label}</span>
              {tab.description ? (
                <span className="mt-0.5 text-meta text-ink-muted">{tab.description}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {tabs.some((tab) => tab.panel != null)
        ? tabs.map((tab) => (
            <div
              key={tab.id}
              id={buildTabPanelId(prefix, tab.id)}
              role="tabpanel"
              aria-labelledby={buildTabId(prefix, tab.id)}
              tabIndex={0}
              hidden={tab.id !== value}
              className={cx(focusRingClass, 'mt-4 rounded-control')}
            >
              {tab.panel}
            </div>
          ))
        : null}
    </div>
  );
}
